import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link as GatsbyLink, useIntl } from 'gatsby-plugin-react-intl';
import { BLOCKS } from '@contentful/rich-text-types';
import SVG from 'react-inlinesvg';
import uuid from 'react-uuid';

import { useFeatureFlag } from '../../../hooks';
import Container from '../Container';
import RichText from '../RichText';
import LanguageToggle from '../LanguageToggle';
import Link from '../Link';
import Image from '../Image';
import AppStores from '../../Custom/AppStores';
import {
  getActiveGroups,
  getCategoryList,
  removeCookies,
} from '../../../utils';

import {
  StyledFooter,
  Logo,
  TopRow,
  SocialLinks,
  BottomRow,
  LeftContainer,
  RightContainer,
  Column,
  AffiliateLinks,
  StyledLink,
  CookieButton,
} from './styles';

const link = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <>{children}</>,
  },
};

const legal = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p className="tiny">{children}</p>,
  },
};

const chunk = (arr, size) =>
  [].concat(
    ...arr.map((_, index) =>
      index % size ? [] : [arr.slice(index, index + size)],
    ),
  );

let mediaQuery;

const Footer = ({ data, languageToggle }) => {
  if (!data) return null;
  const {
    brandLogoDam,
    appButtons,
    socialMediaIcons,
    footerNavLinks,
    legalText,
    affiliateIcons,
  } = data;

  const intl = useIntl();
  const [mobile, setMobile] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const COOKIE_SETTINGS_FLAG = useFeatureFlag('COOKIE_SETTINGS_TOGGLE');

  const matchMedia = (breakpoint = mediaQuery) => {
    if (breakpoint.matches) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  };

  // * Lifecycle to check if mobile / desktop
  useEffect(() => {
    if (typeof window !== 'undefined') {
      mediaQuery = window.matchMedia('(max-width: 691px)');
      mediaQuery.addListener(matchMedia);
      matchMedia();
    }
    return () => {
      mediaQuery.removeListener(matchMedia);
    };
  }, []);

  const getQCGeolocation = () => {
    const geo = window?.OneTrust?.getGeolocationData();
    if (geo)
      setShowCookieSettings(
        geo.state.toUpperCase() === 'QC' && geo.country.toUpperCase() === 'CA',
      );
  };

  // geolocation retrieved from one trust cloudflares cdn
  useEffect(() => {
    if (typeof window !== 'undefined') {
      getQCGeolocation();
      // add event listener to OTSDK wrapper being called
      // dictates when the OneTrust api is available
      window.addEventListener('oneTrustCategoriesChanged', () => {
        getQCGeolocation();
        const activeGroups = getActiveGroups();
        const categoryGroups = getCategoryList();
        removeCookies(categoryGroups, activeGroups);
      });
    }
    return () => {
      window.removeEventListener('oneTrustCategoriesChanged', getQCGeolocation);
    };
  }, []);

  const handleOneTrustButton = () => {
    // eslint-disable-next-line no-undef
    OneTrust.ToggleInfoDisplay();
  };

  let links = [];
  if (footerNavLinks) {
    links = chunk(footerNavLinks, mobile ? 7 : 5);
  }

  return (
    <StyledFooter>
      <Container type="auto-margin">
        <TopRow>
          <LeftContainer>
            <GatsbyLink to="/">
              <Logo
                src={brandLogoDam.asset.localImage.publicURL}
                alt={brandLogoDam?.altText || ''}
              />
            </GatsbyLink>
            {appButtons && <AppStores badges={appButtons} />}
            {socialMediaIcons && (
              <SocialLinks>
                {socialMediaIcons.map(cta => {
                  const src = cta.iconDam
                    ? cta.iconDam.asset.localImage.publicURL
                    : null;
                  return (
                    <Link className="unstyled-link" key={cta.id} data={cta}>
                      <SVG src={src} title={cta.iconDam.altText || ''} />
                    </Link>
                  );
                })}
              </SocialLinks>
            )}
            {affiliateIcons && (
              <AffiliateLinks>
                {affiliateIcons.map(cta => {
                  const src = cta.iconDam ? cta.iconDam : null;
                  return (
                    <StyledLink
                      className="unstyled-link"
                      key={cta.id}
                      data={cta}
                    >
                      <Image src={src} alt={cta.iconDam.altText || ''} />
                      {cta.text && (
                        <RichText document={cta.text} options={link} />
                      )}
                    </StyledLink>
                  );
                })}
              </AffiliateLinks>
            )}
          </LeftContainer>
          <RightContainer>
            {links &&
              links.map(arr => {
                return (
                  <Column key={`link-column-key-${uuid()}`}>
                    {arr.map(cta => {
                      return (
                        <Link className="remove-svg" key={cta.id} data={cta}>
                          <RichText document={cta.label} options={link} />
                        </Link>
                      );
                    })}
                  </Column>
                );
              })}
            {languageToggle && (
              <Column key={`link-column-key-${uuid()}`}>
                <LanguageToggle />
              </Column>
            )}
          </RightContainer>
        </TopRow>
        <BottomRow>
          {legalText && (
            <div>
              <RichText document={legalText} options={legal} />
            </div>
          )}
          {showCookieSettings && COOKIE_SETTINGS_FLAG && (
            <CookieButton type="button" onClick={handleOneTrustButton}>
              {intl.formatMessage({ id: 'cookieSettings' })}
            </CookieButton>
          )}
        </BottomRow>
      </Container>
    </StyledFooter>
  );
};

Footer.propTypes = {
  data: PropTypes.shape().isRequired,
  languageToggle: PropTypes.bool.isRequired,
};

export default Footer;
